import { z } from 'zod';

// The shipment as the Shipments pages show it. The server builds these from the Serb Mind vault.

export const STAGES = [
  'RFQ',
  'Rates Requested',
  'Rates Received',
  'Quoting',
  'Booked',
  'In Transit',
  'Pre-arrival',
  'Delayed',
  'Arrived',
  'Completed',
  'Lost',
  'Cancelled',
  'Merged',
] as const;
export const MODES = ['Air', 'FCL', 'LCL', 'Other'] as const;

const PlaceSchema = z.object({ city: z.string(), code: z.string(), country: z.string() });
const ToneSchema = z.enum(['success', 'warning', 'error', 'info', 'neutral']);

const DetailSchema = z.object({
  story: z.string(),
  situation: z.array(z.tuple([z.string(), z.string()])),
  keyInfo: z.array(z.tuple([z.string(), z.string()])),
  /** the date under each step of the tracker, "" when not reached */
  stepDates: z.array(z.string()),
  timeline: z.array(
    z.object({ when: z.string(), title: z.string(), place: z.string(), tag: z.string(), note: z.string() }),
  ),
  parties: z.array(z.object({ role: z.string(), name: z.string(), place: z.string() })),
  packages: z.array(z.tuple([z.string(), z.string(), z.string()])),
  route: z.array(z.tuple([z.string(), z.string()])),
});

export const ShipmentSchema = z.object({
  srb: z.string(),
  customer: z.string(),
  mode: z.enum(MODES),
  /** "Air Freight", "FCL · 40HC" */
  service: z.string(),
  from: PlaceSchema,
  to: PlaceSchema,
  stage: z.enum(STAGES),
  /** "3 packages · 746 kg · 1.2 CBM" */
  load: z.string(),
  cargo: z.string(),
  incoterm: z.string(),
  etd: z.string(),
  eta: z.string(),
  next: z.object({ text: z.string(), due: z.string(), tone: ToneSchema }),
  messages: z.number(),
  updated: z.string(),
  /** the same date as 2026-10-15 (empty when unknown): what the chase rules count from */
  updatedOn: z.string().optional(),
  agent: z.string(),
  /** the shipping line or airline carrying it; empty until booked */
  carrier: z.string(),
  /** how many times the carrier pushed this booking to a later sailing or flight */
  rollovers: z.number(),
  /** containers (FCL only; 0 otherwise) */
  containers: z.number(),
  /** chargeable weight in kg (air freight only) */
  chargeableKg: z.number(),
  /** volume in CBM (LCL only) */
  cbm: z.number(),
  detail: DetailSchema.optional(),
});

export type Stage = (typeof STAGES)[number];
export type Mode = (typeof MODES)[number];
export type Place = z.infer<typeof PlaceSchema>;
export type Detail = z.infer<typeof DetailSchema>;
export type TimelineEvent = Detail['timeline'][number];
export type Party = Detail['parties'][number];
export type Shipment = z.infer<typeof ShipmentSchema>;
